import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useAppDispatch } from '../../app/hooks';
import Link from 'next/link';
import { useAuth } from '../../context/AuthUserContext';
import { display } from '../../data/display';
import { createReview } from '../../lib/apis/review';
import {
  getFieldErrors,
  getErrorMsg,
  getDateStr,
  getFormData,
  clearObj,
} from '../../lib/utils';
import { createVenueReview } from '../../features/venue/venueSlice';
import { createServiceProviderReview } from '../../features/serviceProvider/serviceProviderSlice';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Icon from '../atoms/Icon';
import Checkbox from '../atoms/Checkbox';
import Logo from '../atoms/Logo';
import Button from '../atoms/Button';
import StepNavi from '../molecules/StepNavi';
import Feedback from '../molecules/Feedback';
import ContentBox from '../molecules/ContentBox';
import Offset from '../organisms/Offset';
import Form from '../molecules/Form';
import Input from '../atoms/Input';
import styles from '../../styles/components/organisms/ItemReviewForm.module.scss';

interface ItemReviewFormProps {
  data: any;
  type: string;
  onComplete: () => void;
}

const ItemReviewForm = (props: ItemReviewFormProps) => {
  const { t } = useTranslation(['common', 'pages', 'rating', 'request']);
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState(0);
  const steps = ['contact', 'event', 'categories', 'description', 'resume'];
  const { data, type, onComplete } = props;
  const planData = display[type]['platinum'] as any;
  const defaultRatings = planData.ratings || {};
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [formData, setFormData] = useState({}) as any;
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const [hoverRate, setHoverRate] = useState({}) as any;
  const [ratings, setRatings] = useState({ ...defaultRatings }) as any;
  const { name, email, terms, role, description } = formData;
  const itemType = t(`rating:${type}`);

  /**
   * Helper function to get overall rating
   */
  const getOverAllRating = (arr: any) => {
    const overall =
      arr.reduce((acc: number, curr: any) => acc + curr.value, 0) /
      arr.filter((obj: any) => obj.value > 0).length;
    return isNaN(overall) ? 0 : overall;
  };

  /**
   * Set ratings data by business type & user role
   */
  const ratingsByTab = ratings[role] || {};
  const ratingsByTabKeys = Object.keys(ratingsByTab);
  const currRatings = ratingsByTabKeys.map((key: string) => {
    const ratingKeys = Object.keys(ratingsByTab[key]);
    const data = ratingKeys.map((el: string, i: number) => {
      return {
        id: el,
        parent: key,
        value: ratings[role][key][el] || 0,
        label: t(`rating:${el}`, { itemType }),
      };
    });
    const value = getOverAllRating(data);
    return {
      id: key,
      label: t(`rating:${key}`),
      value,
      data,
    };
  });
  const overallRating = getOverAllRating(currRatings);

  /**
   * Disable next button in step navi depending on input
   */
  const currStep = steps[step];
  const nextDisabled =
    !!error ||
    (currStep === 'contact' && (!name || !email || !terms)) ||
    (currStep === 'event' && !role) ||
    (currStep === 'description' && !overallRating && !description);

  /**
   * Prefill fields with existing data if available
   */
  useEffect(() => {
    if (!!user) {
      const { name, family_name, email, company } = user;
      const prefillData = {
        name: `${name} ${family_name}`,
        email,
        company,
      };
      setFormData(prefillData);
    }
  }, [user]);

  /**
   * Handle form input change
   */
  const handleChange = (obj: any) => {
    setFormData({ ...formData, ...obj });
    setError(false);
  };

  /**
   * Handle form errors
   */
  const handleError = (error: any) => {
    const errorMsg = getErrorMsg(error);
    switch (errorMsg) {
      default:
        setError(t(`common:form-error`) + ` (${errorMsg})`);
        break;
    }
  };

  /**
   * Handle input of mandatory checkboxes
   */
  const handleCheckbox = (obj: any) => {
    const key = Object.keys(obj)[0];
    const add = obj[key] ? [] : [key];
    const arr = [...fieldErrors.filter((el: string) => el !== key), ...add];
    setFormData({ ...formData, ...obj });
    setFieldErrors(arr);
    if (!!arr.length) {
      setError(t('common:form-input-error'));
    } else {
      setError('');
    }
  };

  /**
   * Set rating state for specific category
   */
  const rate = (obj: any, count: number) => {
    const currRatings = { ...ratings };
    const currRating = currRatings[role][obj.parent][obj.id];
    currRatings[role][obj.parent][obj.id] =
      currRating === 1 && count === 1 ? 0 : count;
    setRatings(currRatings);
  };

  /**
   * Get rewiew object for resume & submit
   */
  const getReviewState = () => {
    const categories = currRatings.reduce(
      (acc: any, curr: any) => ({
        ...acc,
        ...curr.data.reduce(
          (acc2: any, curr2: any) => ({
            ...acc2,
            [curr2.id]: curr2.value,
          }),
          {}
        ),
      }),
      {}
    );
    return clearObj({
      venue_id: type === 'venue' && data.id,
      service_provider_id: type === 'service' && data.id,
      headline: formData.public ? formData.name : 'anonymous',
      event: formData.date,
      contact_type: formData.role.toUpperCase(),
      contact_name: formData.name,
      contact_email: formData.email,
      contact_company: formData.company,
      review: formData.description,
      ...categories,
    });
  };

  /**
   * Submit form data state collected during steps
   */
  const submit = async (formData: any) => {
    if (!!fieldErrors.length) {
      setError(t(`common:form-input-error`));
      return;
    }

    setError(false);
    setLoading(true);

    const payload = getReviewState();

    try {
      const review = await createReview(payload);
      if (!!review?.id) {
        type === 'venue'
          ? dispatch(createVenueReview(review))
          : dispatch(createServiceProviderReview(review));
      }
      // await createReview(payload);
      setComplete(true);
    } catch (err: any) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle step navigation
   */
  const proceed = () => {
    const fieldErrors = getFieldErrors(currStep);
    setFieldErrors(fieldErrors);
    if (!!fieldErrors.length) {
      setError(t('common:form-input-error'));
    } else {
      const currData = getFormData(currStep);
      const mergedData = clearObj({ ...formData, ...currData });
      setFormData(mergedData);
      setError('');
      switch (currStep) {
        case 'resume':
          submit(mergedData);
          break;
        default:
          setLoading(false);
          setStep(step + 1);
          break;
      }
    }
  };

  return (
    <Container className={styles.itemReviewForm}>
      <Row>
        <Col xs={8} sm={6} className={'offset-sm-1'}>
          <h1>
            {complete
              ? t(`rating:form:complete-h1`, { itemType })
              : t(`rating:form:headline`, { itemType })}{' '}
            {!complete && !!overallRating && <b>{overallRating.toFixed(1)}</b>}
          </h1>
          {data.name}
        </Col>
      </Row>
      {complete ? (
        <>
          <Row>
            <Col xs={8} sm={6} className={'offset-sm-1'}>
              <br />
              {/* <Col xs={8} sm={6} className={'offset-sm-1'}> */}
              <h2>{t('rating:form:complete-h2', { itemType })}</h2>
              {/* <p>{t('rating:rating-sent', { itemName: data.name })}</p> */}{' '}
              <br />
            </Col>
            <Col xs={8}>
              <Logo noLink full center />
              <br />
            </Col>
          </Row>
          <Row className={'justify-content-center'}>
            <Col xs={4} sm={2}>
              <Button
                white
                large
                full
                icon="send"
                label={t('common:cta-subscribe')}
                href={t(`pages:newsletter:link`)}
                onClick={onComplete}
              />
            </Col>
            <Col xs={4} sm={2}>
              <Button
                dark
                large
                full
                icon="close"
                label={t('common:cta-close')}
                onClick={onComplete}
              />
            </Col>
          </Row>
        </>
      ) : (
        <Form
          checkOnce
          id={currStep}
          loading={loading}
          onFieldError={(arr) => {
            setFieldErrors(arr);
          }}
          onChange={handleChange}
        >
          {/* Personal data */}

          {currStep === 'contact' && (
            <>
              <Row>
                <Col xs={8} sm={6} className={'offset-sm-1'}>
                  <br />
                  <br />
                  <h2>{t(`rating:form:about-yourself`)}</h2>

                  <Offset>
                    {['name'].map((key, i) => (
                      <Input
                        mandatory
                        reset
                        half
                        key={i}
                        name={key}
                        format={key}
                        icon={key}
                        defaultValue={formData[key] || ''}
                        label={`${t(`common:${key}`)}`}
                        error={fieldErrors.indexOf(key) > -1}
                      />
                    ))}
                  </Offset>

                  <Checkbox
                    grey
                    optional
                    className={styles.checkbox}
                    defaultValue={!!formData['public']}
                    name="public"
                    label={
                      <>
                        <span>{t(`rating:form:name-visible`)}</span>
                        <Icon name="eye" />
                      </>
                    }
                  />

                  <br />

                  <Offset>
                    {['email', 'company'].map((key, i) => (
                      <Input
                        float
                        medium
                        half
                        reset
                        key={i}
                        name={key}
                        format={key}
                        icon={key}
                        mandatory={key !== 'company'}
                        label={t(`common:${key}`)}
                        defaultValue={formData[key] || ''}
                        error={fieldErrors.indexOf(key) > -1}
                      />
                    ))}
                  </Offset>

                  <p> {t(`rating:form:email-not-visible`)}</p>
                  <br />

                  <Checkbox
                    grey
                    className={styles.checkbox}
                    name="terms"
                    error={fieldErrors.indexOf('terms') > -1}
                    defaultValue={!!formData['terms']}
                    onChange={handleCheckbox}
                    label={
                      <>
                        {t('common:accept-terms')}{' '}
                        <Link href="/terms-conditions">
                          <a target="_blank" rel="noopener,noreferrer">
                            {t('pages:terms-conditions:navi')}
                          </a>
                        </Link>
                        *
                      </>
                    }
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Event interaction */}
          {currStep === 'event' && (
            <>
              <Row>
                <Col xs={8} sm={6} className={'offset-sm-1'}>
                  <br />
                  <br />
                  <h2>
                    <Icon name="eye" />
                    <span>
                      {t(`rating:form:last-interaction`, {
                        itemType,
                      })}
                    </span>
                  </h2>

                  {['date'].map((key, i) => (
                    <Input
                      float
                      medium
                      half
                      reset
                      optional
                      key={i}
                      name={key}
                      format={key}
                      icon={key}
                      label={t(`rating:form:${key}`)}
                      defaultValue={formData[key] || ''}
                      error={fieldErrors.indexOf(key) > -1}
                      max={getDateStr('')}
                    />
                  ))}
                </Col>
                <Col xs={8} sm={6} className={'offset-sm-1'}>
                  <br />
                  <br />
                  <h2>
                    <Icon name="eye" />
                    <span>{`${t(`rating:form:interaction-role`, {
                      itemType,
                    })} *`}</span>
                  </h2>

                  {['role'].map((key, i) => (
                    <Input
                      float
                      medium
                      half
                      mandatory
                      key={i}
                      name={key}
                      format={key}
                      icon={'person'}
                      className={styles.select}
                      label={t(`rating:form:choose-${key}`)}
                      error={fieldErrors.indexOf(key) > -1}
                      defaultValue={formData[key] || ''}
                      options={[
                        { value: '', label: t(`rating:form:choose-role`) },
                        { value: 'organizer', label: t(`rating:organizer`) },
                        { value: 'exhibitor', label: t(`rating:exhibitor`) },
                        { value: 'visitor', label: t(`rating:visitor`) },
                      ]}
                    />
                  ))}
                </Col>
              </Row>
            </>
          )}

          {/* Feedback description */}
          {currStep === 'description' && (
            <>
              <Row>
                <Col xs={8} sm={6} className={'offset-sm-1'}>
                  <br />
                  <br />
                  <h2>
                    <Icon name="eye" />
                    <span>
                      {t(`rating:form:description`, { itemType })}
                      {!overallRating && ' *'}
                    </span>
                  </h2>

                  {['description'].map((key, i) => (
                    <Input
                      optional={!!overallRating}
                      mandatory={!overallRating}
                      full
                      float
                      key={i}
                      name={key}
                      icon={key}
                      type={'textarea'}
                      label={t(`common:textarea_placeholder`)}
                      // placeholder={`${t(`common:textarea_placeholder`)} ${
                      //   !overallRating && '*'
                      // }`}
                      error={fieldErrors.indexOf(key) > -1}
                      defaultValue={formData[key] || ''}
                    />
                  ))}
                </Col>
              </Row>
            </>
          )}

          <Row>
            {!!error && (
              <Col xs={8} sm={6} className={'offset-sm-1'}>
                <br />
                <p className={styles.error}>{error}</p>
              </Col>
            )}
          </Row>
        </Form>
      )}

      {/* Star Rating by interaction role */}
      {currStep === 'categories' && (
        <Row>
          <Col xs={8} sm={6} className={'offset-sm-1'}>
            {currRatings.map((el: any, i: number) => (
              <div key={i} className={styles.categories}>
                <br />
                <h2>
                  <Icon name="eye" />
                  <span>
                    {t(`rating:form:please-rate`, {
                      category: t(`rating:${el.id}`),
                    })}
                  </span>
                  {!!el.value && <b>{el.value.toFixed(1)}</b>}
                </h2>
                <br />
                {el.data.map((obj: any, i: number) => (
                  <div key={i} className={styles.category}>
                    <p>{obj.label}</p>
                    <div
                    // className={!!hoverRate.id ? styles.hoverMode : undefined}
                    >
                      {[1, 2, 3, 4, 5].map((num, i) => (
                        <Button
                          key={i}
                          onMouseEnter={() => setHoverRate({ id: obj.id, num })}
                          onMouseLeave={() => setHoverRate({})}
                          className={
                            obj.value >= num
                              ? styles.active
                              : hoverRate.id === obj.id && hoverRate.num >= num
                              ? styles.hover
                              : undefined
                          }
                          icon="star-outline"
                          onClick={() => rate(obj, num)}
                        />
                      ))}
                      <b>{ratings[role][obj.parent][obj.id]}</b>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </Col>
        </Row>
      )}

      {/* Prepare for submit */}
      {currStep === 'resume' && !complete && (
        <Row>
          <Col xs={8} sm={6} className={'offset-sm-1'}>
            <h2>{t(`rating:form:resume`)}</h2>
            <ContentBox slim autoHeight>
              <Feedback
                data={{
                  ...getReviewState(),
                  createdAt: new Date(),
                  rating: overallRating,
                }}
              />
              <br />
            </ContentBox>
          </Col>
        </Row>
      )}

      {!complete && (
        <>
          {currStep !== 'resume' && (
            <Row>
              <Col xs={8} sm={6} className={'offset-sm-1'}>
                <br />
                <div className={styles.footnote}>
                  <span>*</span>
                  <span>{t(`rating:form:mandatory-fields`)}</span>
                </div>
                <div className={styles.footnote}>
                  <Icon name="eye" />
                  <span>{t(`rating:form:info-visible`)}</span>
                </div>
                <br />
                <br />
                <br />
              </Col>
            </Row>
          )}

          {/**
           * Step Navigation
           * */}
          <Row className={`${styles.naviRow} justify-content-center`}>
            <Col xs={7} md={3} xl={2}>
              <StepNavi
                intro={false}
                steps={steps}
                disabled={loading}
                nextDisabled={nextDisabled}
                className={styles.stepNavi}
                endLabel={t(`common:cta-submit`)}
                onNext={proceed}
                onBack={(num) => {
                  setLoading(false);
                  setStep(num);
                }}
                onComplete={proceed}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default ItemReviewForm;
